import { useState, useRef, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import SEO from '@/components/SEO';

// Book cover images
const BOOK_COVERS = {
  front: 'https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/rgvc8ddx_SBT_COVER_FRONT.png',
  back: 'https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/osh78zbj_SBT_COVER_BACK.png',
};

// Chapter content
const CHAPTERS = [
  {
    id: 'prologue',
    title: 'Prologue',
    subtitle: 'The Meeting',
    content: [
      `She had prepared for three weeks.`,
      `Not because the meeting required three weeks. Because preparation was her armour.`,
      `The room was exactly what she expected. Twelve chairs around a mahogany table that had witnessed a hundred years of decisions made by people who looked nothing like her.`,
      `She belonged. She had the numbers, the track record, the credentials that took twice as long to earn and would be scrutinised three times as hard.`,
      `What she had not prepared for was the silence.`,
      `She spoke. Nobody responded.`,
      `She made her point. Someone else made it again, moments later, and the room nodded.`,
      `That night, she did what she always did. She wrote it down. Not for complaint, not for therapy, not even for proof.`,
      `She wrote it down because patterns matter. Because someday, someone would want to understand not just what happened, but how it happened.`,
      `This book is that record.`
    ]
  },
  {
    id: 'chapter-1',
    title: 'Chapter One',
    subtitle: 'What Fifty Years Found',
    content: [
      `Zero.`,
      `That is the number at the centre of this book.`,
      `In fifty years of research on women's leadership development, across thousands of studies and billions of dollars in corporate spending, the number of programs proven to create lasting, systemic change is zero.`,
      `The leadership development industry generates approximately $366 billion annually worldwide.`,
      `Women's representation in senior leadership has increased by approximately 0.5 percentage points per year over the past two decades.`,
      `At this rate, gender parity in corporate leadership will be achieved in approximately 134 years.`,
      `The question is not whether progress has occurred. Some progress has occurred.`,
      `The honest answer, based on the available evidence: probably not.`
    ]
  },
  {
    id: 'chapter-2',
    title: 'Chapter Two',
    subtitle: 'The Nine Labyrinths',
    content: [
      `The labyrinth has nine passages. Each one is documented. Each one is real.`,
      `1. THE PROVE-IT-AGAIN PATTERN — Women must demonstrate competence repeatedly.`,
      `2. THE TIGHTROPE — Women face a narrower range of acceptable behaviour.`,
      `3. THE MATERNAL WALL — Mothers are assumed to be less committed to work.`,
      `4. THE TUG OF WAR — Gender bias can pit women against each other.`,
      `5. THE SPONSORSHIP GAP — Men are 46% more likely to have a sponsor than women.`,
      `6. THE BROKEN RUNG — For every 100 men promoted to manager, only 87 women are.`,
      `7. THE CONFIDENCE MYTHOLOGY — The problem is framed as women's insufficient confidence.`,
      `8. THE NETWORK PENALTY — Professional networks remain heavily gendered.`,
      `9. THE INVISIBLE WORKLOAD — Women perform significantly more "office housework".`
    ]
  },
  {
    id: 'chapter-3',
    title: 'Chapter Three', 
    subtitle: 'What I Built',
    content: [
      `I built She Built This because the stories I kept reading deserved better than a footnote.`,
      `Every profile on this platform is a woman who navigated at least several of the nine passages.`,
      `Five hundred and ninety-five women. Fourteen regions. Seven continents. Thirty industries.`,
      `Each profile is verified. Each fact is sourced. Each photograph is authenticated.`,
      `This is not a celebration, though these women deserve to be celebrated.`,
      `This is documentation. Evidence. Proof.`,
      `Part research. Part record. Part manifesto.`,
      `All of it true. Every fact verified. Every name attached to the work.`,
      `The room changes when someone builds a different one.`,
      `These women built different ones.`
    ]
  }
];

// Page wrapper component with forwardRef for react-pageflip
const PageCover = forwardRef(({ children, className = '' }, ref) => (
  <div ref={ref} className={`page-cover ${className}`}>
    {children}
  </div>
));
PageCover.displayName = 'PageCover';

const PageContent = forwardRef(({ children, number }, ref) => (
  <div 
    ref={ref} 
    className="relative"
    style={{ 
      backgroundColor: '#FFFDF8',
      boxShadow: 'inset 0 0 30px rgba(0,0,0,0.05)',
    }}
  >
    <div className="w-full h-full p-8 flex flex-col" style={{ backgroundColor: '#FFFDF8' }}>
      {children}
    </div>
    {number && (
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <span className="font-mono text-xs text-[#121212]/30 tracking-widest">{number}</span>
      </div>
    )}
  </div>
));
PageContent.displayName = 'PageContent';

const BookReaderPage = () => {
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const bookRef = useRef(null);

  const nextPage = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipNext();
    }
  };

  const prevPage = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipPrev();
    }
  };

  const onFlip = (e) => {
    setCurrentPage(e.data);
  };

  return (
    <div data-testid="book-reader-page" className="min-h-screen bg-[#FAF8F2]">
      <SEO 
        title="She Built This: The Book — by Dr. Neha Dewan"
        description="Read the first three chapters of 'She Built This' by Dr. Neha Dewan. Tap the cover to start reading."
        keywords={['She Built This book', 'Dr. Neha Dewan', 'women leadership', 'interactive book']}
        url="https://shebuiltthis.com/book"
        image={BOOK_COVERS.front}
      />

      {/* Landing Page with Book Cover */}
      {!isReaderOpen && (
        <section className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
          <p className="font-mono text-xs tracking-[0.3em] text-[#121212]/50 mb-10">
            THE BOOK · FIRST EDITION · 2026
          </p>

          {/* Book Cover - Tap to Open */}
          <div 
            className="relative cursor-pointer group mb-10"
            onClick={() => setIsReaderOpen(true)}
            data-testid="book-cover-btn"
          >
            {/* Shadow */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-[85%] h-[20px] bg-black/15 blur-xl rounded-full transition-all group-hover:w-[90%] group-hover:blur-2xl" />
            
            {/* Book Cover Image */}
            <img 
              src={BOOK_COVERS.front}
              alt="She Built This - Book Cover"
              className="w-[280px] md:w-[320px] h-auto relative z-10 transition-transform duration-500 group-hover:scale-[1.02] group-hover:-rotate-1"
              style={{ 
                filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.2))',
              }}
            />
            
            {/* Tap hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
              <div className="bg-white/95 px-6 py-3 shadow-lg">
                <p className="font-mono text-xs tracking-wider text-[#121212]">TAP TO READ</p>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-display text-4xl md:text-5xl text-[#121212] text-center mb-3">
            She Built <span className="text-[#C25E3E]">This</span>
          </h1>

          <p className="font-body text-base text-[#121212]/50 mb-1">by</p>
          <p className="font-display text-xl text-[#121212] mb-8">Dr. Neha Dewan</p>

          <p className="font-body text-base text-[#121212]/60 max-w-md mx-auto text-center leading-relaxed mb-10">
            Fifty years of research. Zero proof that any program works.<br />
            595 women who built empires anyway.
          </p>

          {/* Tap instruction */}
          <p className="font-mono text-xs tracking-wider text-[#C25E3E]">
            ↑ TAP THE BOOK TO START READING
          </p>

          {/* Request Full Book Link */}
          <Link
            to="/book/request"
            className="mt-8 font-mono text-xs tracking-wider text-[#121212]/40 hover:text-[#121212] transition-colors underline underline-offset-4"
          >
            Request the full book
          </Link>
        </section>
      )}

      {/* Interactive Book Reader */}
      {isReaderOpen && (
        <div className="fixed inset-0 z-50 bg-[#FAF8F2] flex flex-col">
          {/* Reader Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#121212]/10">
            <button
              onClick={() => setIsReaderOpen(false)}
              className="flex items-center gap-2 text-[#121212]/60 hover:text-[#121212] transition-colors"
            >
              <X size={20} />
              <span className="font-mono text-sm">Close</span>
            </button>
            
            <p className="font-mono text-xs text-[#C25E3E] tracking-wider">
              PAGE {currentPage + 1}
            </p>
            
            <div className="w-20" />
          </div>

          {/* Book Container */}
          <div className="flex-1 flex items-center justify-center relative px-4 py-8">
            {/* Navigation Arrow Left */}
            <button
              onClick={prevPage}
              className="absolute left-4 md:left-8 z-10 p-3 md:p-4 bg-[#F3EFE9]/5 hover:bg-[#F3EFE9]/10 text-[#121212]/60 hover:text-[#121212] rounded-full transition-all"
              disabled={currentPage === 0}
            >
              <ChevronLeft size={24} />
            </button>

            {/* Flip Book */}
            <HTMLFlipBook
              ref={bookRef}
              width={400}
              height={560}
              size="stretch"
              minWidth={280}
              maxWidth={500}
              minHeight={400}
              maxHeight={700}
              showCover={true}
              mobileScrollSupport={true}
              onFlip={onFlip}
              className="book-shadow"
              drawShadow={true}
              flippingTime={600}
              usePortrait={false}
              maxShadowOpacity={0.3}
              showPageCorners={true}
            >
              {/* Front Cover */}
              <PageCover>
                <img 
                  src={BOOK_COVERS.front} 
                  alt="Front Cover" 
                  className="w-full h-full object-cover"
                />
              </PageCover>

              {/* Title Page */}
              <PageContent number="i">
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <p className="font-mono text-xs tracking-[0.3em] text-[#C25E3E] mb-12">
                    FIRST EDITION · 2026
                  </p>
                  <h1 className="font-display text-4xl text-[#121212] mb-1">SHE</h1>
                  <h1 className="font-display text-4xl text-[#121212] mb-1">BUILT</h1>
                  <h1 className="font-display text-4xl text-[#C25E3E] mb-10">THIS</h1>
                  <div className="w-12 h-px bg-[#F3EFE9]/20 mb-8" />
                  <p className="font-display text-lg text-[#121212] mb-2">Dr. Neha Dewan</p>
                  <p className="font-body text-xs text-[#121212]/50 italic">
                    Founder, She Built This
                  </p>
                </div>
              </PageContent>

              {/* Chapters */}
              {CHAPTERS.map((chapter, idx) => [
                <PageContent key={`${chapter.id}-title`} number={idx * 2 + 1}>
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <p className="font-mono text-xs tracking-[0.3em] text-[#C25E3E] mb-6">
                      {chapter.title.toUpperCase()}
                    </p>
                    <div className="w-12 h-px bg-[#F3EFE9]/20 mb-6" />
                    <h2 className="font-display text-3xl text-[#121212]">{chapter.subtitle}</h2>
                  </div>
                </PageContent>,
                <PageContent key={`${chapter.id}-content`} number={idx * 2 + 2}>
                  <div className="flex-1 overflow-y-auto pr-2">
                    {chapter.content.map((para, pIdx) => (
                      <p 
                        key={pIdx} 
                        className={`font-body text-sm text-[#121212]/80 mb-4 leading-relaxed ${
                          pIdx === 0 ? 'first-letter:text-3xl first-letter:font-display first-letter:text-[#C25E3E] first-letter:float-left first-letter:mr-2 first-letter:mt-1' : ''
                        } ${
                          para.match(/^\d\./) ? 'font-medium text-[#121212] mt-4 text-xs' : ''
                        }`}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </PageContent>
              ]).flat()}

              {/* Back Cover */}
              <PageCover>
                <img 
                  src={BOOK_COVERS.back} 
                  alt="Back Cover" 
                  className="w-full h-full object-cover"
                />
              </PageCover>
            </HTMLFlipBook>

            {/* Navigation Arrow Right */}
            <button
              onClick={nextPage}
              className="absolute right-4 md:right-8 z-10 p-3 md:p-4 bg-[#F3EFE9]/5 hover:bg-[#F3EFE9]/10 text-[#121212]/60 hover:text-[#121212] rounded-full transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Footer hint */}
          <div className="text-center py-3 border-t border-[#121212]/10">
            <p className="font-mono text-xs text-[#121212]/40">
              Click page edges or use arrows to turn pages
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookReaderPage;
